import { useSEO } from "@/lib/seo";
import { ExternalLink } from "lucide-react";

// ---------------------------------------------------------------------------
// Curated legal-links index. Every entry links to a primary source
// (Tennessee Code via Justia, U.S. Code via uscode.house.gov, CFR via
// eCFR, IRS forms & publications, and canonical agency portals).
// ---------------------------------------------------------------------------

const SECTIONS = [
    {
        slug: "wills-trusts-probate-tn",
        number: "01",
        title: "Wills, Trusts & Probate (Tennessee)",
        links: [
            { label: "TN Code Title 30 — Administration of Estates", href: "https://law.justia.com/codes/tennessee/title-30/" },
            { label: "TN Code Title 31 — Descent & Distribution (Intestacy)", href: "https://law.justia.com/codes/tennessee/title-31/" },
            { label: "TN Code Title 32 — Wills", href: "https://law.justia.com/codes/tennessee/title-32/" },
            { label: "TN Code Title 35 — Trusts (Uniform Trust Code)", href: "https://law.justia.com/codes/tennessee/title-35/" },
            { label: "TN Code Title 35, Ch. 16 — Investment Services Act (Asset Protection Trusts)", href: "https://law.justia.com/codes/tennessee/title-35/chapter-16/" }
        ]
    },
    {
        slug: "federal-estate-gift-tax",
        number: "02",
        title: "Federal Estate & Gift Tax",
        links: [
            { label: "26 U.S.C. Ch. 11 — Estate Tax", href: "https://uscode.house.gov/view.xhtml?path=/prelim@title26/subtitleB/chapter11&edition=prelim" },
            { label: "26 U.S.C. Ch. 12 — Gift Tax", href: "https://uscode.house.gov/view.xhtml?path=/prelim@title26/subtitleB/chapter12&edition=prelim" },
            { label: "26 U.S.C. Ch. 13 — Generation-Skipping Transfer Tax", href: "https://uscode.house.gov/view.xhtml?path=/prelim@title26/subtitleB/chapter13&edition=prelim" },
            { label: "26 CFR Part 20 — Estate Tax Regulations", href: "https://www.ecfr.gov/current/title-26/chapter-I/subchapter-B/part-20" },
            { label: "26 CFR Part 25 — Gift Tax Regulations", href: "https://www.ecfr.gov/current/title-26/chapter-I/subchapter-B/part-25" },
            { label: "IRS Form 706 — U.S. Estate (& GST) Tax Return", href: "https://www.irs.gov/forms-pubs/about-form-706" },
            { label: "IRS Form 709 — U.S. Gift (& GST) Tax Return", href: "https://www.irs.gov/forms-pubs/about-form-709" },
            { label: "IRS Publication 559 — Survivors, Executors, and Administrators", href: "https://www.irs.gov/forms-pubs/about-publication-559" }
        ]
    },
    {
        slug: "powers-of-attorney-advance-directives",
        number: "03",
        title: "Powers of Attorney & Advance Directives",
        links: [
            { label: "TN Health Care Decisions Act — TN Code § 68-11-1801 et seq.", href: "https://law.justia.com/codes/tennessee/title-68/chapter-11/part-18/" },
            { label: "TN Uniform Durable Power of Attorney Act — TN Code § 34-6-101 et seq.", href: "https://law.justia.com/codes/tennessee/title-34/chapter-6/" },
            { label: "HIPAA Privacy Rule — 45 CFR Part 160", href: "https://www.ecfr.gov/current/title-45/subtitle-A/subchapter-C/part-160" },
            { label: "HIPAA Privacy Rule — 45 CFR Part 164", href: "https://www.ecfr.gov/current/title-45/subtitle-A/subchapter-C/part-164" }
        ]
    },
    {
        slug: "guardianship-conservatorship",
        number: "04",
        title: "Guardianship & Conservatorship",
        links: [
            { label: "TN Code Title 34, Ch. 1 — General Provisions", href: "https://law.justia.com/codes/tennessee/title-34/chapter-1/" },
            { label: "TN Code Title 34, Ch. 2 — Guardianship of Minors", href: "https://law.justia.com/codes/tennessee/title-34/chapter-2/" },
            { label: "TN Code Title 34, Ch. 3 — Conservatorship of Adults", href: "https://law.justia.com/codes/tennessee/title-34/chapter-3/" },
            { label: "Tennessee Rules of Civil Procedure", href: "https://www.tncourts.gov/rules/rules-civil-procedure" }
        ]
    },
    {
        slug: "medicaid-tenncare-va",
        number: "05",
        title: "Medicaid, TennCare & VA",
        links: [
            { label: "42 U.S.C. Ch. 7, Subch. XIX — Medicaid", href: "https://uscode.house.gov/view.xhtml?path=/prelim@title42/chapter7/subchapter19&edition=prelim" },
            { label: "42 U.S.C. § 1396p — Liens, Adjustments, Transfers, Special Needs Trusts", href: "https://uscode.house.gov/view.xhtml?req=granuleid:USC-prelim-title42-section1396p&num=0&edition=prelim" },
            { label: "42 CFR Ch. IV, Subch. C — CMS Medicaid Regulations", href: "https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-C" },
            { label: "TennCare CHOICES — Long-Term Services & Supports", href: "https://www.tn.gov/tenncare/long-term-services-supports.html" },
            { label: "38 CFR § 3.351 — Total Disability & Aid & Attendance", href: "https://www.ecfr.gov/current/title-38/chapter-I/part-3/subpart-A/section-3.351" },
            { label: "20 CFR Part 416 — Supplemental Security Income", href: "https://www.ecfr.gov/current/title-20/chapter-III/part-416" }
        ]
    },
    {
        slug: "elder-care-agencies",
        number: "06",
        title: "Elder Care Agencies & Protection",
        links: [
            { label: "TN Commission on Aging & Disability", href: "https://www.tn.gov/disability-and-aging.html" },
            { label: "TN Adult Protective Services — TN Code § 71-6-101 et seq.", href: "https://law.justia.com/codes/tennessee/title-71/chapter-6/" },
            { label: "Centers for Medicare & Medicaid Services (CMS)", href: "https://www.cms.gov/" },
            { label: "Social Security Administration (SSA)", href: "https://www.ssa.gov/" },
            { label: "VA — Aid & Attendance and Housebound Benefits", href: "https://www.va.gov/pension/aid-attendance-housebound/" }
        ]
    },
    {
        slug: "real-property-tn",
        number: "07",
        title: "Real Property (Tennessee)",
        links: [
            { label: "TN Code Title 66 — Property", href: "https://law.justia.com/codes/tennessee/title-66/" },
            { label: "TN Uniform Real Property Electronic Recording Act — TN Code Title 66, Ch. 24", href: "https://law.justia.com/codes/tennessee/title-66/chapter-24/" },
            { label: "TN Uniform Residential Landlord & Tenant Act — TN Code § 66-28-101", href: "https://law.justia.com/codes/tennessee/title-66/chapter-28/" },
            { label: "TN Real Estate Broker License Act — TN Code § 62-13-101", href: "https://law.justia.com/codes/tennessee/title-62/chapter-13/" }
        ]
    },
    {
        slug: "federal-real-estate",
        number: "08",
        title: "Federal Real Estate",
        links: [
            { label: "Fair Housing Act — 42 U.S.C. § 3601 et seq.", href: "https://uscode.house.gov/view.xhtml?req=granuleid:USC-prelim-title42-section3601&num=0&edition=prelim" },
            { label: "RESPA — 12 U.S.C. § 2601 et seq.", href: "https://uscode.house.gov/view.xhtml?req=granuleid:USC-prelim-title12-section2601&num=0&edition=prelim" },
            { label: "RESPA Regulation X — 12 CFR Part 1024", href: "https://www.ecfr.gov/current/title-12/chapter-X/part-1024" },
            { label: "Truth in Lending Act — Regulation Z (12 CFR Part 1026)", href: "https://www.ecfr.gov/current/title-12/chapter-X/part-1026" },
            { label: "ADA Title III — 42 U.S.C. § 12181 et seq.", href: "https://uscode.house.gov/view.xhtml?req=granuleid:USC-prelim-title42-section12181&num=0&edition=prelim" }
        ]
    },
    {
        slug: "farm-land",
        number: "09",
        title: "Farm & Land",
        links: [
            { label: "USDA Farm Service Agency", href: "https://www.fsa.usda.gov/" },
            { label: "USDA Natural Resources Conservation Service", href: "https://www.nrcs.usda.gov/" },
            { label: "Farm Credit Administration", href: "https://www.fca.gov/" },
            { label: "Tennessee Department of Agriculture", href: "https://www.tn.gov/agriculture.html" },
            { label: "TN Greenbelt — Agricultural, Forest & Open Space Land — TN Code § 67-5-1004", href: "https://law.justia.com/codes/tennessee/title-67/chapter-5/" }
        ]
    },
    {
        slug: "business-formation-governance",
        number: "10",
        title: "Business Formation & Governance (Tennessee)",
        links: [
            { label: "TN Business Corporation Act — TN Code Title 48, Ch. 11–27", href: "https://law.justia.com/codes/tennessee/title-48/" },
            { label: "TN Revised Limited Liability Company Act — TN Code Title 48, Ch. 249", href: "https://law.justia.com/codes/tennessee/title-48/chapter-249/" },
            { label: "TN Nonprofit Corporation Act — TN Code Title 48, Ch. 51–68", href: "https://law.justia.com/codes/tennessee/title-48/chapter-51/" },
            { label: "TN Uniform Partnership Act — TN Code § 61-1-101 et seq.", href: "https://law.justia.com/codes/tennessee/title-61/chapter-1/" },
            { label: "TN Secretary of State — Business Services", href: "https://sos.tn.gov/businesses/services" }
        ]
    },
    {
        slug: "contracts-commercial",
        number: "11",
        title: "Contracts & Commercial",
        links: [
            { label: "TN Uniform Commercial Code — TN Code Title 47, Ch. 1–9", href: "https://law.justia.com/codes/tennessee/title-47/" },
            { label: "TN Consumer Protection Act — TN Code § 47-18-101 et seq.", href: "https://law.justia.com/codes/tennessee/title-47/chapter-18/" },
            { label: "TN Uniform Trade Secrets Act — TN Code § 47-25-1701 et seq.", href: "https://law.justia.com/codes/tennessee/title-47/chapter-25/part-17/" }
        ]
    },
    {
        slug: "tax-exempt-organizations",
        number: "12",
        title: "Tax-Exempt Organizations",
        links: [
            { label: "26 U.S.C. § 501 — Exemption from Tax on Corporations, etc.", href: "https://uscode.house.gov/view.xhtml?req=granuleid:USC-prelim-title26-section501&num=0&edition=prelim" },
            { label: "26 CFR Part 1, Subch. F — Exempt Organizations", href: "https://www.ecfr.gov/current/title-26/chapter-I/subchapter-F" },
            { label: "IRS Form 1023 — Application for Recognition of Exemption", href: "https://www.irs.gov/forms-pubs/about-form-1023" },
            { label: "IRS Form 1023-EZ — Streamlined Application", href: "https://www.irs.gov/forms-pubs/about-form-1023-ez" },
            { label: "IRS Publication 557 — Tax-Exempt Status for Your Organization", href: "https://www.irs.gov/forms-pubs/about-publication-557" },
            { label: "TN Charitable Solicitations Act — TN Code § 48-101-501 et seq.", href: "https://law.justia.com/codes/tennessee/title-48/chapter-101/part-5/" }
        ]
    },
    {
        slug: "employment",
        number: "13",
        title: "Employment (for small businesses)",
        links: [
            { label: "Fair Labor Standards Act — 29 U.S.C. Ch. 8", href: "https://uscode.house.gov/view.xhtml?path=/prelim@title29/chapter8&edition=prelim" },
            { label: "Family and Medical Leave Act — 29 U.S.C. Ch. 28", href: "https://uscode.house.gov/view.xhtml?path=/prelim@title29/chapter28&edition=prelim" },
            { label: "USCIS Form I-9 — Employment Eligibility Verification", href: "https://www.uscis.gov/i-9" },
            { label: "TN Wage & Overtime — TN Code Title 50", href: "https://law.justia.com/codes/tennessee/title-50/" }
        ]
    },
    {
        slug: "tn-courts-filing-portals",
        number: "14",
        title: "Tennessee Courts & Filing Portals",
        links: [
            { label: "Tennessee Court System", href: "https://www.tncourts.gov/" },
            { label: "Tennessee Rules of Civil Procedure", href: "https://www.tncourts.gov/rules/rules-civil-procedure" },
            { label: "TN Comptroller — Property Assessment", href: "https://comptroller.tn.gov/office-functions/pa.html" }
        ]
    },
    {
        slug: "miscellaneous",
        number: "15",
        title: "Miscellaneous / General",
        links: [
            { label: "Tennessee Bar Association", href: "https://www.tba.org/" },
            { label: "Internal Revenue Service", href: "https://www.irs.gov/" },
            { label: "United States Patent and Trademark Office", href: "https://www.uspto.gov/" },
            { label: "Cornell Legal Information Institute (LII)", href: "https://www.law.cornell.edu/" }
        ]
    }
];

const TOTAL_REFERENCES = SECTIONS.reduce((n, s) => n + s.links.length, 0);

export default function LegalLinks() {
    useSEO({
        title: "Legal Links | Chesterton",
        description:
            "A curated index of the Tennessee and federal statutes, regulations, and agency resources Chesterton attorneys consult most often — estate planning, elder law, real property, and business law.",
        path: "/legal-links"
    });

    return (
        <>
            <section className="bg-acre-bg border-b border-acre-line" data-testid="legal-links-hero">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-16 lg:pt-24 pb-16 lg:pb-20">
                    <div className="flex items-center mb-6">
                        <span className="acre-tick" />
                        <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">Reference</span>
                    </div>
                    <h1 className="font-serif-display text-5xl lg:text-7xl leading-[0.98] tracking-tight text-acre-secondary">
                        Legal links.
                    </h1>
                    <p className="mt-7 max-w-2xl text-base lg:text-lg leading-relaxed text-acre-muted">
                        A curated index of the Tennessee and federal statutes, regulations, and agency resources Chesterton attorneys consult most often. Organized by subject. These are external primary sources, not a substitute for legal advice.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-8 text-[11px] uppercase tracking-[0.28em] text-acre-muted">
                        <span data-testid="legal-links-count-sections">{SECTIONS.length} · Categories</span>
                        <span data-testid="legal-links-count-references">{TOTAL_REFERENCES} references</span>
                    </div>
                </div>
            </section>

            <section className="bg-white border-b border-acre-line" data-testid="legal-links-toc">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 lg:py-14">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-acre-muted mb-6">On this page</div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-8">
                        {SECTIONS.map((s) => (
                            <li key={s.slug}>
                                <a
                                    href={`#${s.slug}`}
                                    data-testid={`toc-${s.slug}`}
                                    className="group flex items-baseline gap-3 text-acre-ink hover:text-acre-secondary transition-colors"
                                >
                                    <span className="text-[11px] tracking-widest text-acre-muted group-hover:text-acre-secondary">
                                        {s.number}
                                    </span>
                                    <span className="text-[15px] leading-snug">{s.title}</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <div className="bg-acre-bg">
                {SECTIONS.map((s, idx) => (
                    <section
                        key={s.slug}
                        id={s.slug}
                        data-testid={`legal-section-${s.slug}`}
                        className={`scroll-mt-24 border-b border-acre-line ${idx % 2 === 0 ? "bg-white" : "bg-acre-bg"}`}
                    >
                        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                            <div className="lg:col-span-4">
                                <div className="text-[11px] uppercase tracking-[0.28em] text-acre-muted mb-4">
                                    {s.number}
                                </div>
                                <h2 className="font-serif-display text-3xl lg:text-4xl text-acre-secondary leading-tight">
                                    {s.title}
                                </h2>
                                <div className="mt-3 text-[11px] uppercase tracking-[0.24em] text-acre-muted">
                                    {s.links.length} {s.links.length === 1 ? "reference" : "references"}
                                </div>
                            </div>
                            <div className="lg:col-span-8">
                                <ul className="divide-y divide-acre-line border-t border-acre-line">
                                    {s.links.map((l) => (
                                        <li key={l.href}>
                                            <a
                                                href={l.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`link-${s.slug}-${l.label.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
                                                className="group flex items-start gap-4 py-4 text-acre-ink hover:text-acre-secondary transition-colors"
                                            >
                                                <span className="flex-1 text-[15px] lg:text-base leading-snug">
                                                    {l.label}
                                                </span>
                                                <ExternalLink
                                                    size={14}
                                                    strokeWidth={1.5}
                                                    className="mt-1 shrink-0 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                                                />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            <section className="bg-acre-primary text-white" data-testid="legal-links-disclaimer">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-14 lg:py-16">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-white/70 mb-4">Note</div>
                    <p className="max-w-3xl text-base lg:text-lg leading-relaxed text-white/90">
                        These references reflect published rules and materials as of the date the reader consults them. External links may change without notice. Nothing on this page is legal advice, and consulting a linked resource does not create an attorney–client relationship with Chesterton.
                    </p>
                </div>
            </section>
        </>
    );
}
